import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { DenounceAdminService } from '../../../services/denounce-admin.service';

type Metrics = Awaited<ReturnType<DenounceAdminService['getMetrics']>> extends import('rxjs').Observable<infer R> ? R : never;

const STORAGE_KEY_COLLAPSED = 'denounce_metrics_panel_collapsed';

const STATUS_LABELS: Record<string, string> = {
  RECIBIDA: 'Recibidas',
  EN_INVESTIGACION: 'En investigación',
  CONCLUIDA: 'Concluidas',
  DESESTIMADA: 'Desestimadas',
};

const STATUS_COLORS: Record<string, string> = {
  RECIBIDA: '#2563eb',
  EN_INVESTIGACION: '#f59e0b',
  CONCLUIDA: '#16a34a',
  DESESTIMADA: '#9ca3af',
};

@Component({
  selector: 'app-denounce-metrics-panel',
  templateUrl: './denounce-metrics-panel.component.html',
  styleUrls: ['./denounce-metrics-panel.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DenounceMetricsPanelComponent implements OnInit {
  loading = false;
  collapsed = false;

  metrics: Metrics | null = null;

  dateFrom = '';
  dateTo = '';

  lineChartData: ChartData<'line'> | null = null;
  statusChartData: ChartData<'bar'> | null = null;

  lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  statusChartOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { grid: { display: false } },
    },
  };

  topOperators: { operatorName: string; total: number }[] = [];

  constructor(
    private denounceService: DenounceAdminService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    try {
      this.collapsed = localStorage.getItem(STORAGE_KEY_COLLAPSED) === '1';
    } catch {
      this.collapsed = false;
    }

    if (!this.collapsed) {
      this.reload();
    }
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem(STORAGE_KEY_COLLAPSED, this.collapsed ? '1' : '0');
    } catch {}
    if (!this.collapsed && !this.metrics) {
      this.reload();
    }
    this.cdr.markForCheck();
  }

  reload(): void {
    this.loading = true;
    this.cdr.markForCheck();
    this.denounceService.getMetrics(this.dateFrom || undefined, this.dateTo || undefined).subscribe({
      next: (res) => {
        this.metrics = res;
        this.loading = false;
        this.buildCharts();
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  applyDateRange(): void {
    this.reload();
  }

  clearDateRange(): void {
    this.dateFrom = '';
    this.dateTo = '';
    this.reload();
  }

  private buildCharts(): void {
    if (!this.metrics) return;

    // Serie temporal (line chart)
    const labels = this.metrics.dailySeries.map((d) => d.day);
    const data = this.metrics.dailySeries.map((d) => d.count);
    this.lineChartData = {
      labels,
      datasets: [
        {
          data,
          label: 'Denuncias creadas',
          fill: true,
          backgroundColor: 'rgba(28, 89, 61, 0.15)',
          borderColor: '#1C593D',
          tension: 0.3,
          pointRadius: 2,
          pointBackgroundColor: '#1C593D',
        },
      ],
    };

    // Por estado (barras horizontales)
    const statusEntries = this.metrics.totalsByStatus;
    const statusOrder = ['RECIBIDA', 'EN_INVESTIGACION', 'CONCLUIDA', 'DESESTIMADA'];
    const sortedEntries = [...statusEntries].sort(
      (a, b) => statusOrder.indexOf(a.label) - statusOrder.indexOf(b.label),
    );
    this.statusChartData = {
      labels: sortedEntries.map((e) => STATUS_LABELS[e.label] || e.label),
      datasets: [
        {
          data: sortedEntries.map((e) => e.count),
          backgroundColor: sortedEntries.map((e) => STATUS_COLORS[e.label] || '#9ca3af'),
          borderRadius: 4,
          barThickness: 22,
        },
      ],
    };

    // Top operadores (máx 3 con denuncias asignadas, excluye "Sin asignar")
    this.topOperators = this.metrics.assignmentLoad
      .filter((a) => a.operatorId !== null)
      .slice(0, 3)
      .map((a) => ({ operatorName: a.operatorName, total: a.total }));
  }

  getStatusCount(status: string): number {
    if (!this.metrics) return 0;
    return this.metrics.totalsByStatus.find((s) => s.label === status)?.count || 0;
  }

  formatHours(h: number | null): string {
    if (h === null || h === undefined) return '—';
    if (h < 24) return `${h}h`;
    const days = Math.round(h / 24);
    return `${days}d`;
  }
}
